import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth';
import { UserMenu } from '../../components/UserMenu';
import { Wordmark } from '../../components/Wordmark';
import { SharedFooter } from '../../components/SharedFooter';
import type { JobListing } from '@modelcast/types';
import { JobCard } from './JobCard';
import type { ApplyState } from './JobCard';
import { ApplyModal } from './ApplyModal';
import { authHeaders } from '../../shared/authHeaders';
import { useCurrentProfile } from '../../shared/CurrentProfileProvider';

export function JobBoardPage() {
  const navigate = useNavigate();
  const { getToken, isLoaded, isSignedIn } = useAuth();
  const { profile, isLoading: isProfileLoading } = useCurrentProfile();
  const [jobs, setJobs] = useState<JobListing[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedJob, setSelectedJob] = useState<JobListing | null>(null);
  const isApproved = !isSignedIn
    ? false
    : isProfileLoading
      ? null
      : Boolean(profile?.isModel && profile.applicationStatus === 'approved');

  const fetchJobs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await getToken();
      const response = await fetch('/api/jobs', {
        headers: authHeaders(token),
      });
      if (response.ok) {
        const data = await response.json();
        setJobs(data.jobs);
      } else {
        setError(response.status === 403
          ? 'Only approved models can view jobs.'
          : 'Failed to load job listings');
      }
    } catch (err) {
      console.error('Error fetching jobs:', err);
      setError('Failed to load job listings');
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => {
    const loadJobsForApprovedModel = async () => {
      setError(null);
      setJobs([]);

      if (!isLoaded) {
        setLoading(true);
        return;
      }

      if (!isSignedIn) {
        setLoading(false);
        return;
      }

      if (isProfileLoading) {
        setLoading(true);
        return;
      }

      setLoading(true);
      if (!isApproved) {
        setJobs([]);
        setLoading(false);
        return;
      }

      await fetchJobs();
    };

    void loadJobsForApprovedModel();
  }, [fetchJobs, isApproved, isLoaded, isProfileLoading, isSignedIn]);

  const handleApply = (job: JobListing) => {
    if (!isSignedIn) {
      navigate('/sign-in');
      return;
    }
    if (!isApproved) {
      navigate('/models/apply');
      return;
    }
    setSelectedJob(job);
  };

  const handleApplicationSubmitted = () => {
    setSelectedJob(null);
    fetchJobs(); // Refresh to update hasApplied status
  };

  const applyState: ApplyState = isSignedIn
    ? isApproved === null
      ? 'checking'
      : isApproved
        ? 'approved'
        : 'needs-approval'
    : 'signed-out';

  return (
    <div className="min-h-screen bg-bg">
      {/* Nav */}
      <nav className="sticky top-0 z-50 bg-bg/88 backdrop-blur-[14px] border-b border-line">
        <div className="page-container h-[72px] flex items-center gap-7">
          <Wordmark />
          <div className="flex gap-7 ml-7">
            <Link to="/" className="text-[13.5px] text-text-dim hover:text-text">
              Discover
            </Link>
            <span className="text-[13.5px] text-text border-b border-text pb-0.5">
              Jobs
            </span>
            <Link to="/dashboard" className="text-[13.5px] text-text-dim hover:text-text">
              Host
            </Link>
          </div>
          <div className="flex-1" />
          <div className="flex items-center gap-3">
            {isSignedIn ? (
              <UserMenu />
            ) : (
              <Link
                to="/sign-in"
                className="text-[13.5px] text-text-dim hover:text-text"
              >
                Sign in
              </Link>
            )}
          </div>
        </div>
      </nav>

      {/* Header */}
      <header className="py-[72px] pb-12 border-b border-line">
        <div className="page-container">
          <div className="page-eyebrow">
            Model opportunities
          </div>
          <h1 className="page-heading mb-[22px]">
            Find your next session.
          </h1>

          <div className="mt-7 flex flex-wrap gap-3">
            {!isSignedIn && (
              <>
              <Link
                to="/sign-in"
                className="btn btn-accent btn-lg"
              >
                Sign in to apply
              </Link>
              <Link
                to="/models/apply"
                className="btn btn-secondary btn-lg"
              >
                Become a model
              </Link>
              </>
            )}
          </div>

          {isSignedIn && isApproved === false && (
            <div className="mt-7">
              <Link
                to="/models/apply"
                className="btn btn-accent btn-lg"
              >
                Apply to become a model
              </Link>
              <p className="mt-3 text-sm text-text-dim italic">
                You need to be an approved model to apply for jobs.
              </p>
            </div>
          )}
        </div>
      </header>

      {/* Jobs Grid */}
      <section className="py-16">
        <div className="page-container">
          {!isSignedIn ? (
            <div className="max-w-xl rounded-xl border border-line bg-panel p-6">
              <h2 className="font-display text-2xl font-normal text-text">Model approval required.</h2>
              <p className="mt-2 text-sm leading-6 text-text-dim">
                Sign in with an approved model account to view job opportunities.
              </p>
              <div className="mt-5 flex flex-wrap gap-3">
                <Link to="/sign-in" className="btn btn-accent">
                  Sign in
                </Link>
                <Link to="/models/apply" className="btn">
                  Apply to model
                </Link>
              </div>
            </div>
          ) : isApproved === false ? (
            <div className="max-w-xl rounded-xl border border-line bg-panel p-6">
              <h2 className="font-display text-2xl font-normal text-text">Model approval required.</h2>
              <p className="mt-2 text-sm leading-6 text-text-dim">
                Only approved models can view and apply to jobs.
              </p>
              <Link to="/models/apply" className="btn btn-accent mt-5">
                Apply to become a model
              </Link>
            </div>
          ) : loading ? (
            <p className="text-text-dim">Loading opportunities...</p>
          ) : error ? (
            <p className="text-red-600">{error}</p>
          ) : jobs.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-text-dim mb-2">No open positions right now.</p>
              <p className="text-sm text-text-faint italic">Check back soon — studios post new opportunities regularly.</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {jobs.map((job) => (
                  <JobCard
                    key={job.room.id}
                    job={job}
                    onApply={() => handleApply(job)}
                    applyState={applyState}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      <SharedFooter />

      {/* Apply Modal */}
      {selectedJob && (
        <ApplyModal
          job={selectedJob}
          onClose={() => setSelectedJob(null)}
          onSubmit={handleApplicationSubmitted}
        />
      )}
    </div>
  );
}
